import React, { createContext, useContext, useEffect, useState } from "react";

/* Cross-chrome UI state: the mobile sidebar drawer + desktop collapse. */
const UIContext = createContext();

export function useUI() {
    return useContext(UIContext);
}

const COLLAPSE_KEY = "sm-sidebar-collapsed";

export function UIProvider({ children }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Desktop collapse (icon rail) — persisted across sessions.
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try {
            return localStorage.getItem(COLLAPSE_KEY) === "1";
        } catch (e) {
            return false;
        }
    });

    useEffect(() => {
        document.documentElement.setAttribute(
            "data-sidebar",
            sidebarCollapsed ? "collapsed" : "expanded"
        );
        try {
            localStorage.setItem(COLLAPSE_KEY, sidebarCollapsed ? "1" : "0");
        } catch (e) { /* storage unavailable */ }
    }, [sidebarCollapsed]);

    // Esc closes the drawer (keyboard accessibility).
    useEffect(() => {
        if (!sidebarOpen) return undefined;
        const onKey = e => {
            if (e.key === "Escape") setSidebarOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [sidebarOpen]);

    // Lock body scroll while the drawer is open (mobile).
    useEffect(() => {
        if (!sidebarOpen) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, [sidebarOpen]);

    return (
        <UIContext.Provider
            value={{
                sidebarOpen,
                openSidebar: () => setSidebarOpen(true),
                closeSidebar: () => setSidebarOpen(false),
                toggleSidebar: () => setSidebarOpen(o => !o),
                sidebarCollapsed,
                expandSidebar: () => setSidebarCollapsed(false),
                toggleSidebarCollapsed: () => setSidebarCollapsed(c => !c),
            }}
        >
            {children}
        </UIContext.Provider>
    );
}
